/**
 * Marketplace/listing identity helpers.
 *
 * Сейчас проект исторически использует Ozon offer_id как главный ключ товара.
 * Для будущих маркетплейсов держим отдельный канонический ключ listing_key,
 * не меняя legacy `__key`, которым уже пользуются таблицы, задачи и кэш.
 */

export type Marketplace = "ozon" | "wb";

export const DEFAULT_MARKETPLACE: Marketplace = "ozon";
export const LEGACY_PRODUCT_KEY_SEP = ":";
const LISTING_KEY_SEP = ":";

export function isMarketplace(value: unknown): value is Marketplace {
  return value === "ozon" || value === "wb";
}

export function normalizeMarketplace(value: unknown): Marketplace {
  return isMarketplace(value) ? value : DEFAULT_MARKETPLACE;
}

export function legacyProductKey(offerId: string, storeId?: string | null): string {
  return storeId ? `${storeId}${LEGACY_PRODUCT_KEY_SEP}${offerId}` : offerId;
}

export function makeListingKey(params: {
  marketplace?: Marketplace | null;
  storeId: string;
  listingId: string;
}): string {
  const marketplace = params.marketplace ?? DEFAULT_MARKETPLACE;
  return [
    marketplace,
    params.storeId,
    encodeURIComponent(params.listingId),
  ].join(LISTING_KEY_SEP);
}

export function parseListingKey(key: string): {
  marketplace: Marketplace;
  storeId: string;
  listingId: string;
} | null {
  const [marketplaceRaw, storeId, encodedListingId, ...rest] = key.split(LISTING_KEY_SEP);
  if (!marketplaceRaw || !storeId || !encodedListingId || rest.length > 0) return null;
  if (!isMarketplace(marketplaceRaw)) return null;
  try {
    return {
      marketplace: marketplaceRaw,
      storeId,
      listingId: decodeURIComponent(encodedListingId),
    };
  } catch {
    return null;
  }
}
